// Display helpers for the Squareme customer panel and Watchtower escalations.

const CURRENCY_SYMBOLS = { NGN: '₦', USD: '$', GBP: '£', EUR: '€' };
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Formats squareme-next money `{ amount: '<minor units>', currency }` with
 * string arithmetic only (no floats): `{ amount: '123456', currency: 'NGN' }`
 * becomes `₦1,234.56`.
 * @param {{amount: string, currency: string}} money
 * @returns {string}
 */
export const formatMoney = money => {
  if (!money || money.amount === null || money.amount === undefined) return '';
  const raw = String(money.amount);
  const negative = raw.startsWith('-');
  const digits = negative ? raw.slice(1) : raw;
  if (!/^\d+$/.test(digits)) return '';

  const padded = digits.padStart(3, '0');
  const whole = padded.slice(0, -2).replace(/^0+(?=\d)/, '');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const value = `${grouped}.${padded.slice(-2)}`;
  const symbol = CURRENCY_SYMBOLS[money.currency];
  const text = symbol ? `${symbol}${value}` : `${money.currency} ${value}`;
  return negative ? `-${text}` : text;
};

export const isZeroMoney = money =>
  !money || /^-?0*$/.test(String(money.amount));

/**
 * The payload each escalation type takes. `source` says where the target
 * comes from: the matched customer, one of their KYC submissions, or an
 * item from their activity (whose `reference` is the subject id).
 */
export const ESCALATION_TYPES = {
  'customer.freeze': { idField: 'customerId', source: 'customer' },
  'customer.unfreeze': { idField: 'customerId', source: 'customer' },
  'kyc.review_decision': {
    idField: 'submissionId',
    source: 'kyc',
    choice: { field: 'decision', options: ['approve', 'reject'] },
  },
  'transfers.reverse': {
    idField: 'transferId',
    source: 'activity',
    itemTypes: ['transfer_out', 'transfer_in'],
  },
  'payouts.resolve': {
    idField: 'payoutId',
    source: 'activity',
    itemTypes: ['bank_withdrawal'],
    choice: { field: 'outcome', options: ['completed', 'failed'] },
  },
  'bills.resolve': {
    idField: 'billId',
    source: 'activity',
    itemTypes: ['bill_payment'],
    choice: { field: 'outcome', options: ['delivered', 'failed'] },
  },
  'debit_cards.release_hold': {
    idField: 'authorizationId',
    source: 'activity',
    itemTypes: ['card_purchase', 'card_purchase_adjusted'],
  },
};

/** i18n key segment for an action type: `customer.freeze` -> `CUSTOMER_FREEZE`. */
export const actionTypeKey = actionType =>
  actionType.replace(/[^a-z0-9]+/gi, '_').toUpperCase();

/** Whether an activity item names a subject an escalation can target. */
export const hasSubject = item =>
  !!item && UUID.test(item.reference || '') && item.reference !== item.id;

/**
 * The escalation an activity item can start, if any:
 * `{ actionType, payload }` with the item's subject id filled in.
 */
export const escalationForItem = (item, allowedTypes = []) => {
  if (!hasSubject(item)) return null;
  const actionType = Object.keys(ESCALATION_TYPES).find(
    type =>
      allowedTypes.includes(type) &&
      (ESCALATION_TYPES[type].itemTypes || []).includes(item.type)
  );
  if (!actionType) return null;
  return {
    actionType,
    payload: { [ESCALATION_TYPES[actionType].idField]: item.reference },
  };
};

/**
 * Where an escalation stands, for the agent:
 * sending | awaiting | approved | rejected | expired | refused | send_failed | not_done.
 * A send that never reached Watchtower can be retried. `refused` (the target
 * cannot be acted on) and `not_done` (approved, but the target could no
 * longer be acted on) are final; `last_error` says why.
 */
export const escalationState = escalation => {
  switch (escalation.status) {
    case 'pending':
      return 'sending';
    case 'proposed':
      return 'awaiting';
    case 'failed':
      return escalation.decided_at ? 'not_done' : 'send_failed';
    default:
      return escalation.status;
  }
};

/** Match outcome: matched | not_verified (not an app chat) | no_match. */
export const matchState = match =>
  match?.customerId ? 'matched' : match?.reason || 'no_match';
